import {Component, OnInit, TemplateRef, viewChild, ViewChild, LOCALE_ID, Inject} from '@angular/core';
import {AdminAnalyticsService, Trend} from 'app/admin-analytics/admin-analytics.service';
import {
  AnalyticsData,
  AnalyticsQuery,
  FilterReport,
  ReportConfig,
  ReportRecord,
  Term,
  TimeData
} from 'app/admin-analytics/admin-analytics.datamodel';
import {NotificationsService} from 'web-common/src/app/notifications.service';
import {ActivatedRoute} from '@angular/router';
import {Observable} from 'rxjs';
import moment from 'moment';
import * as FileSaver from 'file-saver';
import { DateRange } from '@angular/material/datepicker';
import { User } from 'web-common/src/app/user/user';
import { Title } from '@angular/platform-browser';
import { DateAdapter } from '@angular/material/core';
declare var google: any;

@Component({
    selector: 'app-admin-analytics',
    templateUrl: './admin-analytics.component.html',
    styleUrls: ['./admin-analytics.component.less'],
    standalone: false
})
export class AdminAnalyticsComponent implements OnInit {

  @ViewChild('googleChartContainer') googleChartContainer: any;

  selectedRangeValue: DateRange<Date> | undefined;
  activeDateRangeValue: DateRange<Date> | undefined;

  allTime = [
    {
      'label': $localize`All Time`,
      'value': 'All Time'
    }, {
      'label': $localize`Past Year`,
      'value': 'now-365d'
    }, {
      'label': $localize`Past 90 days`,
      'value': 'now-90d'
    }, {
      'label': $localize`Past 30 days`,
      'value': 'now-30d'
    }
  ];

  allDepartments : Term[] = [
    new Term(0, 'All Areas Of Focus', $localize`All Areas Of Focus`)
  ];

  allRoles : Term[] = [
    new Term(0, 'All Roles', $localize`All Roles`)
  ];

  allDisciplines : Term[] = [
    new Term(0, 'All Disciplines', $localize`All Disciplines`)
  ];

  USERS_REPORT_PATH: string = 'users';
  REFERENCES_REPORT_PATH: string = 'references';
  roleOptions = [];
  areaOptions = [];
  additionalOptions = [];
  isRoleIndeterminate = false;
  isAreaIndeterminate = false;
  isAdditionalIndeterminate = false;
  showAdditionalProfileInformation = false;
  additionalProfileInformationLabel: string;
  adminUser: User;
  historicalUsageKey: string;
  disableApplyButton = true;
  showFilters: boolean = false;
  showSpinner: boolean = false;
  isEmptyData: boolean = false;
  disabled: boolean = false;
  startDate: any;
  endDate: any;
  textStartDate: any;
  textEndDate: any;
  selectedTimeFrame: any;
  selectedTime : any;
  selectedRole : Term;
  selectedDiscipline : Term;
  selectedDepartment : Term;
  publications : Term[];
  eventAction : string;
  chartInstance : any;
  googleChartData : any;
  chartData: Array<Object>;
  chartDataFormated: Array<Object>;
  viewData : TimeData[];
  minDate: { usageEvent: Date, otherEvents: Date } = { usageEvent: null, otherEvents: null };
  maxDate: Date;
  timeFrame: string;
  grouping: string;
  metric: string;
  gaFilters = {
    startDate: '',
    endDate: moment().subtract(1, 'days').format('YYYY-MM-DD'),
    dimension: 'date',
    filter: 'customEvent:inst_group_id',
    filterValue: ''
  };
  reportFilters: FilterReport = {
    trend: 'month',
    roles: [],
    areaOfFocus: [],
    additionalProfileInformation: [],
    dateFrom: 0,
    dateTo: Math.trunc(moment().toDate().getTime() / 1000)
  };
  calendarOpen = false;

  localesSelectedTimeFrame = {
    'mtd': $localize`This month`,
    'lmtd': $localize`Last month`,
    'ytd': $localize`This year`,
    'lytd': $localize`Last 12 months`,
    'custom': $localize`Custom`
  }

  @ViewChild('toasterGAInvalidDateRange', { static: true }) toasterGAInvalidDateRange: TemplateRef<any>;

  noteActive = false;

  private getChartLocale(locale: string): string {
    const localeMap = {
      'zh-cn': 'zh-CN',
      'zh-tw': 'zh-TW'
    };
    return localeMap[locale.toLowerCase()] || locale || 'en';
  }

  constructor(
    private analyticsService : AdminAnalyticsService,
    private route : ActivatedRoute,
    private title: Title,
    private notifications: NotificationsService,
    private dateAdapter: DateAdapter<Date>,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.title.setTitle($localize`${this.route.snapshot.data['title']}` + ' - EndNote Fusion');
    this.dateAdapter.setLocale(this.locale);

    google.charts.load('current', {
      packages: ['corechart'],
      language: this.getChartLocale(this.locale)
    });
  }

  ngOnInit() {
    const discountGroupID = this.route.parent.snapshot.data['user'].discountGroup.id;
    this.adminUser = this.route.parent.snapshot.data['user'] as User;
    this.historicalUsageKey = this.adminUser.discountGroup.historicalUsageKey;
    this.metric = 'screenPageViews';
    this.gaFilters.filterValue = discountGroupID.substr(discountGroupID.indexOf(':') + 1);
    this.grouping = 'month';
    this.minDate.usageEvent = moment().set({year: 2023, month: 3, date: 1}).toDate();
    this.minDate.otherEvents = moment().set({year: 2017, month: 5, date: 1}).toDate();
    this.maxDate = moment().subtract(1, 'days').toDate();
    this.timeFrame = 'lytd';
    this.setEventAction();
    this.selectedTime = this.allTime[0];
    this.selectedTimeFrame = this.localesSelectedTimeFrame['lytd']
    this.selectedDepartment = this.allDepartments[0];
    this.selectedDiscipline = this.allDisciplines[0];
    this.selectedRole = this.allRoles[0];
    if (this.eventAction) {
      this.getAnalytics().subscribe(data => this.populate(data));
    }
  }

  /**
   * Populates the component with the data passed to it.
   * @param data
   */
  populate(data : AnalyticsData) {
    this.allDepartments = this.allDepartments.concat(data.filters.departments
      .map(department => new Term(department.count, department.name)));
    this.allRoles = this.allRoles.concat(data.filters.roles
      .map(role => new Term(role.count, role.name)));
    this.allDisciplines = this.allDisciplines.concat(data.filters.disciplines
      .map(discipline => new Term(discipline.count, discipline.name)));
    this.publications = data.publications;
    this.viewData = data.views;
    this.chartData = data.views.map(function (obj) {
      return {x: obj.time, y: obj.count};
    });
    if (this.chartInstance) {
      this.updateChart();
    }
  }

  getAnalytics() : Observable < AnalyticsData > {
    this.isEmptyData = false;
    this.showSpinner = true;
    let query = new AnalyticsQuery();
    query.department = this.selectedDepartment.name;
    query.discipline = this.selectedDiscipline.name;
    query.eventAction = this.eventAction;
    query.role = this.selectedRole.name;
    query.temporal = this.selectedTime.value;

    return this.analyticsService.search(query);
  }

  onSelect(type, value) {
    switch (type) {
      case 'time':
        this.selectedTime = value;
        break;
      case 'role':
        this.selectedRole = value;
        break;
      case 'discipline':
        this.selectedDiscipline = value;
        break;
      case 'department':
        this.selectedDepartment = value;
        break;
    }
    this.getAnalytics().subscribe(data => {
      this.publications = data.publications;
      this.viewData = data.views;
      this.chartData = data.views.map(function (obj) {
        return {x: obj.time, y: obj.count};
      });
    });
  }

  onResize(event) {
    this.updateChart();
  }

  getMinDate() : Date {
    if (this.eventAction == null) {
      return this.minDate.usageEvent;
    }
    return this.minDate.otherEvents;
  }

  getUsagesMetricName() {
    return this.metric === 'screenPageViews' ? $localize`Pageviews` : $localize`Sessions`;
  }

  showDownloadDropdown() {
    return this.eventAction == null && this.historicalUsageKey != null && this.historicalUsageKey.length > 0;
  }

  downloadHistoricalReport() {
    const handleError = (error: any) => {
      console.error('Error downloading the file: ', error);
      this.notifications.error('Error downloading the file');
    };
    this.analyticsService.getGAHistoricalDataUrl().subscribe( data => {
      this.analyticsService.getGAHistoricalDataFile(data, handleError);
    }, handleError);
  }

  downloadReport() {
    const config : ReportConfig = this.getReportConfig(this.eventAction);
    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      headers: config.headers,
      useBom: true
    };

    const downloadFileCallback = (blobData) => {
      // Added this CJK character '\ufeff' to get proper encoding for CJK characters
      const blob = new Blob(['\ufeff' + blobData], {type: 'text/csv;charset=utf-8;'});
      const reportName = this.generateFileName(this.eventAction, this.grouping, this.reportFilters.dateFrom, this.reportFilters.dateTo);
      FileSaver.saveAs(blob, reportName + '.csv');
    }

    const handleError = (error: any) => {
      console.error('Error downloading the file: ', error);
      this.notifications.error('Error downloading the file');
    };

    let data: ReportRecord[];
    if (this.eventAction === null) {
      data = this.googleChartData.dataTable.map(d => {
        let dateFormated;
        if (this.timeFrame === 'mtd') {
          this.grouping = 'day';
        }
        if (this.grouping === 'month') {
          dateFormated = new Date(d[0]).toLocaleDateString('default', { month: 'numeric', year: 'numeric' });
        } else {
          dateFormated = new Date(d[0]).toLocaleString('default', { day: 'numeric', month: 'short', year: 'numeric' });
        }
        return new ReportRecord(d[1], dateFormated);
      })
      this.dataToCsv(data, config, true);
    } else if (this.eventAction === 'accounts') {
      this.analyticsService.getAccountsCreatedReportCSV(
          this.reportFilters.trend === 'day' ? Trend.Day : Trend.Month,
          this.reportFilters.roles,
          this.reportFilters.areaOfFocus,
          this.reportFilters.additionalProfileInformation,
          this.reportFilters.dateFrom,
          this.reportFilters.dateTo)
        .subscribe(downloadFileCallback, handleError);
    } else if (this.eventAction === 'refs') {
      this.analyticsService.getReferencesAddedReportCSV(
          this.reportFilters.trend === 'day' ? Trend.Day : Trend.Month,
          this.reportFilters.roles,
          this.reportFilters.areaOfFocus,
          this.reportFilters.additionalProfileInformation,
          this.reportFilters.dateFrom,
          this.reportFilters.dateTo)
        .subscribe(downloadFileCallback, handleError);
    } else {
      data = this.publications.map(d => new ReportRecord(d.count, d.name));
      this.dataToCsv(data, config);
    }
  }

  dataToCsv(data: ReportRecord[], config: ReportConfig, identifierFirst: boolean = false) {
    // Added this CJK character '\ufeff' to get proper encoding for CJK characters
    let csv = '\ufeff' + config.headers.join(',') + '\n';
    if (identifierFirst) {
      data.forEach(row => {
        csv += `"${row.identifier}",${row.count}\n`;
      });
    } else {
      data.forEach(row => {
        csv += `${row.count},"${row.identifier}"\n`;
      });
    }
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(blob, config.title.replace(' ', '_') + '.csv');
  }

  activate() {
    if (this.noteActive) {
      this.noteActive = false;
    } else {
      this.noteActive = true;
    }
  }

  getDisabledClass() {
    const classes = [];
    if (this.disabled) {
      classes.push('disabled');
      classes.push('toggle-selected');
    }
    return classes;
  }

  private hasEmptyData() {
    if (!this.chartData || this.chartData.length === 0) {
      this.isEmptyData = true;
      this.showSpinner = false;
      return true;
    } else {
      let dataCounter = 0;
      this.chartData.map(function (obj) {
        dataCounter += obj[1];
      });
      if (dataCounter === 0) {
        this.isEmptyData = true;
        this.showSpinner = false;
        return true;
      }
      this.isEmptyData = false;
      return false;
    }
  }

  private updateChart() {

    if (this.hasEmptyData()) {
      return;
    }

    if (this.timeFrame === 'mtd' || this.timeFrame === 'lmtd' || this.disabled) {
      this.grouping = 'day';
    }

    let startDate;
    let endDate;
    let metricName;
    switch (this.eventAction) {
      case 'accounts':
        metricName = $localize`Accounts`;
        startDate = new Date(this.reportFilters.dateFrom * 1000);
        endDate = new Date(this.reportFilters.dateTo * 1000);
        break;
      case 'refs':
        metricName = $localize`References`;
        startDate = new Date(this.reportFilters.dateFrom * 1000);
        endDate = new Date(this.reportFilters.dateTo * 1000);
        break;
      default:
        metricName = this.getUsagesMetricName();
        startDate = this.gaFilters.startDate;
        endDate = this.gaFilters.endDate;
        break;
    }

    let xAxisLabel = this.grouping === 'month' ? $localize`Monthly` : $localize`Daily`;
    let tooltipDateFormat = this.grouping === 'month' ? 'MMMM yyyy' : 'EEEE, MMM dd, yyyy';
    let xDateformat = this.grouping === 'month' ? 'MMM yyyy' : 'MMM dd, yy';
    let countDataValues = this.chartData.reduce((sum, data) => sum + data[1], 0);
    let valuesYaxis = countDataValues > 0 ? null : [0, 100, 200, 300, 400, 500];
    let valuesXaxis = null;

    if (this.grouping === 'month' && this.timeFrame !== 'mtd' && !this.dateRangeInOneMonth(moment(startDate), moment(endDate))) {
      let dataGrouped = this.groupDataByMonthYear(this.chartData);
      this.chartDataFormated = dataGrouped.map(obj => {
        return [this.formatDateByMonthYear(obj[0]), obj[1]];
      });
      if (this.chartDataFormated.length < 6) {
        valuesXaxis = this.chartDataFormated.map( data => {
          return data[0];
        })
      }
      this.initializeChart(metricName, xAxisLabel, xDateformat, tooltipDateFormat, valuesYaxis, valuesXaxis);
      this.googleChartData.dataTable.push(...this.chartDataFormated);
    } else {
      if (this.chartData.length < 8) {
        valuesXaxis = this.chartData.map( data => {
          return data[0];
        })
      }
      this.initializeChart(metricName, xAxisLabel, xDateformat, tooltipDateFormat, valuesYaxis, valuesXaxis);
      this.googleChartData.dataTable.push(...this.chartData);
    }

    this.showSpinner = false;
  }

  private formatDateByMonthYear(date: string): Date {
    let separator: string | undefined;
    const nonDigitChars = date.match(/[^\d]/g);
    if (date.includes('/')) {
      separator = '/';
    } else if (date.includes('.')) {
      separator = '.';
    } else if (nonDigitChars) {
      separator = nonDigitChars[0];
    } else {
      throw new Error('Invalid date format. No separator found.');
    }
    const parts = date.split(separator);
    let month = parseInt(parts[0], 10);
    let year = parseInt(parts[1], 10);
    if (isNaN(month) || isNaN(year)) {
      throw new Error('Invalid month or year in the date string.');
    }
    // Swap month and year if they seem to be in the wrong positions
    if (year <= 12 || month > 12) {
      const temp = month;
      month = year;
      year = temp;
    }
    const dateObj = new Date(year, month - 1, 1);
    return dateObj;
  }

  private groupDataByMonthYear(data: any[]): any[] {
    const groupedData = {};

    data.forEach(item => {
      const month = new Date(item[0]).toLocaleString('default', { month: 'numeric', year: 'numeric' });
      if (groupedData[month]) {
        groupedData[month] += item[1];
      } else {
        groupedData[month] = item[1];
      }
    });

    return Object.entries(groupedData).map(([month, sum]) => [month, sum]);
  }

  private initializeChart(metricName, xAxisLabel, xDateformat, tooltipDateFormat, valuesYaxis, valuesXaxis) {
    const maxValue = Math.max(...this.chartData.map(i => i[1]));
    const chartWidth = this.googleChartContainer ? this.googleChartContainer.nativeElement.offsetWidth : 800;
    this.googleChartData =  {
      chartType: 'ColumnChart',
      columns: ['Date', metricName],
      dataTable: [],
      formatters: [
        {
          formatter: new google.visualization.DateFormat({pattern: tooltipDateFormat}),
          colIndex: 0,
        }
      ],
      options: {
        height: 600,
        width: chartWidth,
        backgroundColor: '#F5F5F5',
        chartArea: {width: '90%', height: '70%'},
        theme: 'material',
        hAxis: {
          ticks: valuesXaxis,
          format: xDateformat,
          title: xAxisLabel,
          titleTextStyle: {
            fontName: 'Roboto',
            fontSize: 18,
            italic: false,
          },
          textStyle: {
            color: '#222',
            fontName: 'Roboto',
            fontSize: 14,
          },
          gridlines: {
            color: '#F5F5F5',
            count: 13,
          }
        },
        vAxis: {
          ticks: valuesYaxis,
          textStyle: {
            color: '#222',
            fontName: 'Roboto',
            fontSize: 14,
          },
          gridlines: {
            count: maxValue < 20 ? maxValue + 1 : 8,
          },
          format: '0'
        },
        legend: 'none',
        colors: ['#4C5C92']
      },
    };
  }

  private setInitialDates(tf) {
    let currentDate = moment();
    if (tf === 'lytd') {
      currentDate.subtract(12, 'months');
    } else if (tf === 'ytd') {
      if (currentDate.month() === 0) {
        currentDate.subtract(1, 'years');
      } else {
        currentDate.startOf('year');
      }
    } else if (tf === 'lmtd') {
      currentDate.subtract(1, 'months').startOf('month');
    } else {
      if (currentDate.date() === 1) {
        currentDate.subtract(1, 'months');
      } else {
        currentDate.startOf('month');
      }
    }

    if (currentDate.isBefore('2017-06-01')) { // Do not show any data prior to 06/01/2017
      currentDate.set({ 'year' : 2017, 'month' : 5, 'date' : 1 });
    }
    return currentDate.format('YYYY-MM-DD');
  }

  private formatDate(date) {
    return moment(date.substr(0, 4) + '-' + date.substr(4, 2) + '-' + date?.substr(6)).valueOf();
  }

  private formatYearMonthDate(date) {
    return moment(date.substr(0, 4) + '-' + date.substr(4, 2)).valueOf();
  }

  private dateRangeInOneMonth(startDate, endDate): boolean {
    if (startDate.isSame(endDate, 'month') && startDate.isSame(endDate, 'year')) {
      return true;
    }

    const diffDays = Math.abs(endDate.diff(startDate, 'days'));
    return diffDays <= 30;
  }

  private getGAData() {
    this.isEmptyData = false;
    this.showSpinner = true;
    const self = this;
    this.analyticsService.getGAData(this.gaFilters, this.metric).subscribe( data => {
      let finalData = data.rows;
      // If we have no data, we don't need to add empty months. We will display "No data found" message.
      if (this.reportFilters.trend === 'month' && finalData.length > 0) {
        finalData = this.addEmptyMonths(data.rows);
      }
      this.chartData = finalData.map(function (obj) {
        return [new Date(self.formatDate(obj[0])), Number(obj[1])];
      });
      this.updateChart();
    }, function(error) {
      self.notifications.error(self.generateHTMLString('', self.toasterGAInvalidDateRange));
    });
  }

  private addEmptyMonths(data) {
    const self = this;
    const dateMap = new Map();
    let result = data;

    for (const obj of data) {
      if (obj[0].length < 6) {
        return result;
      }
      const year = obj[0].substring(0, 4);
      const month = obj[0].substring(4, 6);
      const yearMonth = `${year}-${month}`;
      dateMap.set(yearMonth, 0);
    }

    const [startYear, startMonth] = this.gaFilters.startDate.split('-').map(Number);
    const [endYear, endMonth] = this.gaFilters.endDate.split('-').map(Number);
    let currentYear = startYear;
    let currentMonth = startMonth;

    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
      const key = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;

      if (!dateMap.has(key)) {
        const firstDayOfMonth = `${key}-01`;
        result.push([firstDayOfMonth.replace(/-/g, ''), '0']);
      }

      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    result.sort(function (a, b) {
      const dateA = new Date(self.formatDate(a[0])).getTime();
      const dateB = new Date(self.formatDate(b[0])).getTime();
      return dateA - dateB;
    });

    return result;
  }

  private getReportData(report) {
    this.isEmptyData = false;
    this.showSpinner = true;
    const self = this;
    this.analyticsService.getReportData(report, this.reportFilters).subscribe( data => {
      const objectsArray = Object.entries(data).map(([k, v]) => ([ k,v ]));
      this.chartData = objectsArray.map(function (obj) {
        if (self.reportFilters.trend === 'month') {
          return [new Date(self.formatYearMonthDate(obj[0])), Number(obj[1])];
        } else {
          return [new Date(self.formatDate(obj[0])), Number(obj[1])];
        }
      });
      this.updateChart()
    }, function(error) {
      self.notifications.error(self.generateHTMLString('', self.toasterGAInvalidDateRange));
    });
  }

  handleKeyPress(event: KeyboardEvent, option: string) {
    if (event.key === 'Enter') {
      this.onMetricChange(option);
    }
  }

  onClearDateRange() {
    this.selectedRangeValue = undefined;
    this.activeDateRangeValue = undefined;
    this.textStartDate = undefined;
    this.textEndDate = undefined;
   }

  public onDateRangeChange() {
    if (this.startDate && this.endDate) {
      this.textStartDate = this.selectedRangeValue.start;
      this.textEndDate = this.selectedRangeValue.end;
      this.activeDateRangeValue = this.selectedRangeValue;
      this.disabled = false;
      if (this.dateRangeInOneMonth(moment(this.startDate), moment(this.endDate))) {
        this.grouping = 'day';
        this.reportFilters.trend = 'day';
        this.disabled = true;
      }
      if (!this.eventAction) {
        this.gaFilters.startDate = moment(this.startDate).format('YYYY-MM-DD');
        this.gaFilters.endDate = moment(this.endDate).format('YYYY-MM-DD');
        this.getGAData();
      }
      if (this.eventAction === 'accounts' || this.eventAction === 'refs') {
        this.reportFilters.dateFrom = Math.trunc(moment(this.startDate).utc(true).toDate().getTime() / 1000);
        this.reportFilters.dateTo = Math.trunc(moment(this.endDate).utc(true).add(23, 'hours').add(59, 'minutes').toDate().getTime() / 1000);
        this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
      }
    }
    this.calendarOpen = false;
  }

  public onTimeFrameChange(timeFrame) {
    this.timeFrame = timeFrame;
    this.disabled = false;
    switch (timeFrame) {
      case 'mtd':
        this.disabled = true;
        this.grouping = 'day';
        this.selectedTimeFrame = this.localesSelectedTimeFrame['mtd'];
        if (!this.eventAction) {
          this.gaFilters.startDate = moment().set({
            year: moment().year(),
            month: moment().month(),
            date: 1
          }).format('YYYY-MM-DD');
          this.gaFilters.endDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
          this.getGAData();
        }
        if (this.eventAction === 'accounts' || this.eventAction === 'refs') {
          this.reportFilters.trend = 'day';
          this.reportFilters.dateFrom = Math.trunc(moment().utc().startOf('month').toDate().getTime() / 1000);
          this.reportFilters.dateTo = Math.trunc(moment().toDate().getTime() / 1000);
          this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
        }
        break;
      case 'lmtd':
        this.disabled = true;
        this.grouping = 'day';
        this.selectedTimeFrame = this.localesSelectedTimeFrame['lmtd'];
        if (!this.eventAction) {
          this.gaFilters.startDate = moment().subtract(1, 'months').set({
            date: 1
          }).format('YYYY-MM-DD');
          this.gaFilters.endDate = moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
          this.getGAData();
        }
        if (this.eventAction === 'accounts' || this.eventAction === 'refs') {
          this.reportFilters.trend = this.grouping;
          this.reportFilters.dateFrom = Math.trunc(moment().utc().subtract(1, 'months').startOf('month').toDate().getTime() / 1000);
          this.reportFilters.dateTo = Math.trunc(moment().utc().subtract(1, 'months').endOf('month').toDate().getTime() / 1000);
          this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
        }
        break;
      case 'ytd':
        this.selectedTimeFrame = this.localesSelectedTimeFrame['ytd'];
        if (!this.eventAction) {
          this.gaFilters.startDate = moment().set({
            year: moment().year(),
            month: 0,
            date: 1
          }).format('YYYY-MM-DD');
          this.gaFilters.endDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
          this.getGAData();
        }
        if (this.eventAction === 'accounts' || this.eventAction === 'refs') {
          this.reportFilters.trend = this.grouping;
          this.reportFilters.dateFrom = Math.trunc(moment().utc().startOf('year').toDate().getTime() / 1000);
          this.reportFilters.dateTo = Math.trunc(moment().toDate().getTime() / 1000);
          this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
        }
        break;
      case 'lytd':
        this.selectedTimeFrame = this.localesSelectedTimeFrame['lytd'];
        if (!this.eventAction) {
          this.gaFilters.startDate = moment().subtract(12, 'months').subtract(1, 'days').format('YYYY-MM-DD');
          this.gaFilters.endDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
          this.getGAData();
        }
        if (this.eventAction === 'accounts' || this.eventAction === 'refs') {
          this.reportFilters.trend = this.grouping;
          this.reportFilters.dateFrom = Math.trunc(moment().utc().subtract(12, 'months').startOf('day').toDate().getTime() / 1000);
          this.reportFilters.dateTo = Math.trunc(moment().toDate().getTime() / 1000);
          this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
        }
        break;
      default: // custom
        this.selectedTimeFrame = this.localesSelectedTimeFrame['custom'];
        if (this.startDate && this.endDate) {
          this.onDateRangeChange();
        } else {
          this.disabled = true;
        }
        break;
    }
  }

  public onMetricChange(metric) {
    this.metric = metric;
    this.getGAData();
  }

  public onGroupingChange(groupBy) {
    if (this.timeFrame !== 'mtd' && !this.disabled) {
      this.grouping = groupBy;
      if (!this.eventAction) {
        this.updateChart();
      }
      if (this.eventAction === 'accounts' || this.eventAction === 'refs') {
        this.reportFilters.trend = groupBy;
        this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
      }
    }
  }

  private setEventAction() {
    this.route.url.subscribe(url => {
      this.eventAction = this.route.snapshot['data'].eventAction;
      if (!this.eventAction) {
        this.gaFilters.startDate = this.setInitialDates(this.timeFrame);
        this.getGAData();
      }
      if (this.eventAction === 'accounts' || this.eventAction === 'refs') {
        this.loadDropdownsFilters();
        this.reportFilters.dateFrom = Math.trunc(new Date(this.setInitialDates(this.timeFrame)).getTime() / 1000);
        this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
      }
    });
  }

  private generateHTMLString(string, node): string {
    return string += node.nativeElement.innerHTML;
  }

  private getReportConfig(action : string) : ReportConfig {
    const date = moment().format('YYYY-MM-DD');

    switch (action) {
      case 'add':
        return new ReportConfig([ 'Times Added', 'Publication' ], date + '-Publications Added');
      case 'cite':
        return new ReportConfig([ 'Times Cited', 'Publication' ], date + '-Publications Cited');
      case 'view':
        return new ReportConfig([ 'Times Viewed', 'Publication' ], date + '-Publications Viewed');
      case 'read':
        return new ReportConfig([ 'Times Read', 'Publication' ], date + '-Publications Read');
      case 'accounts':
        return new ReportConfig([ 'Accounts Created', 'Publication' ], date + '-Accounts Created');
      case 'refs':
        return new ReportConfig([ 'References Added', 'Publication' ], date + '-References Added');
      case null:
        const metricName = this.getUsagesMetricName();
        return new ReportConfig([
          'Date', metricName
        ], date + '-Usage-Analytics-' + metricName);
    }
  }

  private generateFileName(reportType: string, trend: string, dateFrom: number, dateTo: number): string {
    const fromDate = new Date(dateFrom * 1000);
    const toDate = new Date(dateTo * 1000);

    const formattedFromDate = this.formatDateReport(fromDate);
    const formattedToDate = this.formatDateReport(toDate);

    const typePrefix = reportType === 'refs' ? 'References' : 'Accounts';
    const trendSuffix = trend === 'day' ? '-byDay' : '-byMonth';
    const fileName = `${typePrefix}${trendSuffix}-${formattedFromDate}-${formattedToDate}`;

    return fileName;
  }

  private formatDateReport(date: Date): string {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  }


  selectedChange(m: any) {
    if (!this.selectedRangeValue?.start || this.selectedRangeValue?.end) {
      this.selectedRangeValue = new DateRange<Date>(m, null);
    } else {
      const start = this.selectedRangeValue.start;
      const end = m;
      if (end < start) {
        this.selectedRangeValue = new DateRange<Date>(end, start);
      } else {
        this.selectedRangeValue = new DateRange<Date>(start, end);
      }
    }
    this.startDate = this.selectedRangeValue.start;
    this.endDate = this.selectedRangeValue.end;

    if (this.selectedRangeValue.start) {
      this.textStartDate = this.selectedRangeValue.start;
      this.textEndDate = this.selectedRangeValue.end;
    }
  }

  closeDatepicker() {
    this.selectedRangeValue = this.activeDateRangeValue;
    this.textStartDate = this.selectedRangeValue?.start || undefined;
    this.textEndDate = this.selectedRangeValue?.end || undefined;
    this.startDate = this.textStartDate;
    this.endDate = this.textEndDate;
    this.calendarOpen = false;
  }

  openDatepicker() {
    this.calendarOpen = true;
    if ((this.textStartDate != this.startDate) && (this.textEndDate != this.endDate)) {
      this.startDate = this.textStartDate;
      this.endDate = this.textEndDate;
      this.selectedRangeValue = new DateRange<Date>(this.startDate, this.endDate);
    }
  }

  showSharedFilters(): void {
    this.showFilters = !this.showFilters;
  }

  loadDropdownsFilters(): void {
    const discountGroupResponse = this.route.parent.snapshot.data['user'].discountGroup as {
      roles: string[];
      areaOfFocus: string[];
    };

    this.roleOptions = [
      { label: 'All', checked: true },
      ...discountGroupResponse.roles.map(role => ({ label: role, checked: true }))
    ];

    this.areaOptions = [
      { label: 'All', checked: true },
      ...discountGroupResponse.areaOfFocus.map(area => ({ label: area, checked: true }))
    ];

    this.showAdditionalProfileInformation = !!this.adminUser.discountGroup.additionalProfileInformation &&
      !!this.adminUser.discountGroup.additionalProfileInformation.label;

    if (this.showAdditionalProfileInformation) {
      this.additionalProfileInformationLabel = this.adminUser.discountGroup.additionalProfileInformation.label;

      if (this.adminUser.discountGroup.additionalProfileInformation.answers) {
        this.additionalOptions = [
          { label: 'All', checked: true },
          ...this.adminUser.discountGroup.additionalProfileInformation.answers.map(answer => ({ label: answer, checked: true }))
        ];
      }
    } else {
      this.additionalProfileInformationLabel = '';
      this.additionalOptions = [];
    }
  }

  private selectedSummary(options): string {
    const selectedCount = options.slice(1).filter(option => option.checked).length;

    if (selectedCount === options.length - 1) {
      return $localize` (all)`;
    } else if (selectedCount > 0) {
      return ` (${selectedCount})`;
    } else {
      return '';
    }
  }

  selectedRolesSummary(): string {
    return this.selectedSummary(this.roleOptions);
  }

  selectedAreaSummary(): string {
    return this.selectedSummary(this.areaOptions);
  }

  selectedAdditionalSummary(): string {
    return this.selectedSummary(this.additionalOptions);
  }

  onRoleOptionChange(newValue: boolean, option: any) {
    const labelToUpdate = option.label;
    const updatedRoleOptions = this.roleOptions.map(opt =>
      opt.label === labelToUpdate ? { ...opt, checked: newValue } : opt
    );

    const { listToUpdate, isIndeterminate } = this.updateAllCheckboxState(newValue, option, [...updatedRoleOptions]);
    this.roleOptions = listToUpdate;
    this.isRoleIndeterminate = isIndeterminate;
  }

  onAreaOptionChange(newValue: boolean, option: any) {
    const labelToUpdate = option.label;
    const updatedAreaOptions = this.areaOptions.map(opt =>
      opt.label === labelToUpdate ? { ...opt, checked: newValue } : opt
    );

    const { listToUpdate, isIndeterminate } = this.updateAllCheckboxState(newValue, option, [...updatedAreaOptions]);
    this.areaOptions = listToUpdate;
    this.isAreaIndeterminate = isIndeterminate;
  }

  onAdditionalOptionChange(newValue: boolean, option: any) {
    const labelToUpdate = option.label;
    const updatedAdditionalOptions = this.additionalOptions.map(opt =>
      opt.label === labelToUpdate ? { ...opt, checked: newValue } : opt
    );

    const { listToUpdate, isIndeterminate } = this.updateAllCheckboxState(newValue, option, [...updatedAdditionalOptions]);
    this.additionalOptions = listToUpdate;
    this.isAdditionalIndeterminate = isIndeterminate;
  }

  private updateAllCheckboxState(newValue: boolean, option: any, listToUpdate: any[]) {
    const allOption = listToUpdate[0];
    const checkedOptions = listToUpdate.slice(1).filter(opt => opt.checked);
    const allUnchecked = listToUpdate.slice(1).every(opt => !opt.checked);

    let isIndeterminate = false;

    if (option.label === 'All') {
      listToUpdate.forEach(opt => opt.checked = newValue);
      isIndeterminate = false;
    } else {
      if (allOption.checked && newValue === true) {
        listToUpdate.forEach(opt => opt.checked = true);
      } else if (allUnchecked) {
        allOption.checked = false;
        isIndeterminate = false;
      } else {
        const allChecked = checkedOptions.length === listToUpdate.length - 1;
        allOption.checked = allChecked;
        isIndeterminate = !allChecked && !allUnchecked;
      }
    }

    return { listToUpdate, isIndeterminate };
  }

  clearAllFilters(): void {
    this.roleOptions.forEach(opt => (opt.checked = true));
    this.areaOptions.forEach(opt => (opt.checked = true));
    this.additionalOptions.forEach(opt => (opt.checked = true));
    this.disableApplyButton = true;
    this.reportFilters.roles = [];
    this.reportFilters.areaOfFocus = [];
    this.reportFilters.additionalProfileInformation = [];
    this.isRoleIndeterminate = false;
    this.isAreaIndeterminate = false;
    this.isAdditionalIndeterminate = false;
    this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
  }

  filtersChanged(): boolean {
    const listsToCheck = [this.roleOptions, this.areaOptions, this.additionalOptions];
    return listsToCheck.some(list => list.some(opt => !opt.checked));
  }

  applyFilters(): void {
    if (this.eventAction === 'accounts' || this.eventAction === 'refs') {
      this.reportFilters.roles = this.getSelectedFilters(this.roleOptions);
      this.reportFilters.areaOfFocus = this.getSelectedFilters(this.areaOptions);
      this.reportFilters.additionalProfileInformation = this.getSelectedFilters(this.additionalOptions);
      this.getReportData(this.eventAction === 'refs' ? this.REFERENCES_REPORT_PATH : this.USERS_REPORT_PATH);
    }
  }

  getSelectedFilters(optionList): string[] {
    if (optionList.length > 0 && optionList[0].checked) {
      return [];
    }
    return optionList
      .filter(option => option.checked)
      .map(option => option.label);
  }

}
